import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  ArrowBackRounded as ArrowBackIcon,
  AccessTimeRounded as TimeIcon
} from '@mui/icons-material';
import { patientService } from '../services/patientService';
import { testService } from '../services/testService';
import { Patient, GENDER_OPTIONS } from '../types/patient';
import { Test, getTestData, getTestTypeCode, getClassificationsFromTest, isScoreBasedTest } from '../types/test';
import AddTestModal from '../components/AddTestModal';
import ObservationsModal from '../components/ObservationsModal';
import { LoadingOrb } from '../components/LoadingOrb';
import { sileo } from 'sileo';

const PatientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<any>({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    gender: 'M',
    status: 'activo',
    phone: '',
    address: '',
    educationYears: 12
  });
  const [tests, setTests] = useState<Test[]>([]);
  const [showAddTestModal, setShowAddTestModal] = useState(false);
  const [testsLoading, setTestsLoading] = useState(false);
  const [showObservationsModal, setShowObservationsModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);

  // Cargar datos del paciente
  useEffect(() => {
    const loadPatient = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await patientService.getPatientById(parseInt(id));
        setPatient(response);
        setEditData({
          firstName: response.firstName,
          lastName: response.lastName,
          email: response.email || '',
          dateOfBirth: response.dateOfBirth,
          gender: response.gender,
          status: response.status,
          phone: response.phone || '',
          address: response.address || ''
        });
      } catch (error: any) {
        setError(error.response?.data?.message || 'Error al cargar el paciente');
      } finally {
        setLoading(false);
      }
    };

    loadPatient();
  }, [id]);

  // Cargar pruebas del paciente
  useEffect(() => {
    const loadTests = async () => {
      if (!id) return;
      
      try {
        setTestsLoading(true);
        const response = await testService.getPatientTests(parseInt(id));
        setTests(response);
      } catch (error: any) {
        console.error('Error cargando pruebas:', error);
      } finally {
        setTestsLoading(false);
      }
    };

    loadTests();
  }, [id]);

  // Inicializar editData cuando se carga el paciente
  useEffect(() => {
    if (patient) {
      setEditData({
        firstName: patient.firstName || '',
        lastName: patient.lastName || '',
        email: patient.email || '',
        dateOfBirth: patient.dateOfBirth || '',
        gender: patient.gender || 'M',
        status: patient.status || 'activo',
        phone: patient.phone || '',
        address: patient.address || '',
        educationYears: patient.educationYears || null
      });
    }
  }, [patient]);

  // Manejar cambios en los campos de edición
  const handleEditChange = (field: string, value: any) => {
    setEditData((prev: any) => ({ ...prev, [field]: value }));
  };

  // Guardar cambios
  const handleSave = async () => {
    if (!patient) return;
    
    try {
      await patientService.updatePatient(patient.id, editData);
      setPatient((prev: Patient | null) => prev ? { ...prev, ...editData } : null);
      setIsEditing(false);
      sileo.success({
        title: 'Paciente actualizado',
        description: 'Los cambios se guardaron correctamente.',
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al actualizar el paciente';
      setError(errorMessage);
      sileo.error({
        title: 'No se pudo actualizar el paciente',
        description: errorMessage,
      });
    }
  };

  // Cancelar edición
  const handleCancel = () => {
    setEditData({
      firstName: patient?.firstName || '',
      lastName: patient?.lastName || '',
      email: patient?.email || '',
      dateOfBirth: patient?.dateOfBirth || '',
      gender: patient?.gender || 'M',
      status: patient?.status || 'activo',
      phone: patient?.phone || '',
      address: patient?.address || '',
      educationYears: patient?.educationYears || null
    });
    setIsEditing(false);
  };

  // Manejar prueba agregada
  const handleTestAdded = (newTest: Test) => {
    setTests(prev => [newTest, ...prev]);
    setShowAddTestModal(false);
    sileo.success({
      title: 'Prueba agregada',
      description: 'La prueba se registró en el historial del paciente.',
    });
  };

  // Mostrar observaciones
  const handleShowObservations = (test: Test) => {
    setSelectedTest(test);
    setShowObservationsModal(true);
  };

  // Manejar actualización de test
  const handleTestUpdated = (updatedTest: Test) => {
    setTests(prev => prev.map(t => t.id === updatedTest.id ? updatedTest : t));
    setSelectedTest(updatedTest);
  };

  // Parsear fecha "YYYY-MM-DD" (DATEONLY del backend) como fecha local, sin
  // pasar por UTC. `new Date("YYYY-MM-DD")` interpreta el string como medianoche
  // UTC y, al formatear en una zona horaria detrás de UTC (ej. Chile), retrocede
  // un día. Construir con año/mes/día evita ese corrimiento.
  const parseDateOnly = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Calcular edad
  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = parseDateOnly(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  // Formatear fecha
  const formatDate = (dateString: string) => {
    return parseDateOnly(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Obtener etiqueta de género
  const getGenderLabel = (gender: string) => {
    const option = GENDER_OPTIONS.find(opt => opt.value === gender);
    return option?.label || gender;
  };

  // Formatear fecha de creación/actualización
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <LoadingOrb size={64} minHeight="400px" />
      </Container>
    );
  }

  if (error || !patient) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Paciente no encontrado'}
          </Alert>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/patients"
          >
            Volver a Pacientes
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ pt: 0, pb: 3 }}>
        {/* Volver */}
        <Button
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/patients"
          size="small"
          sx={{ mb: 1, color: 'text.secondary' }}
        >
          Volver a Pacientes
        </Button>

        {/* Ficha del paciente */}
        <Card sx={{ mb: 3, borderLeft: (theme) => `4px solid ${theme.palette.primary.main}` }}>
          <CardContent sx={{ py: 2.5 }}>
            <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2}>
              <Box>
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                    {patient.firstName} {patient.lastName}
                  </Typography>
                  <Chip
                    label={patient.status === 'activo' ? 'Activo' : 'Inactivo'}
                    color={patient.status === 'activo' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  RUT {patient.identificationNumber} · {getGenderLabel(patient.gender)} · {calculateAge(patient.dateOfBirth)} años
                  {patient.evaluator && ` · Evaluador: ${patient.evaluator.firstName} ${patient.evaluator.lastName}`}
                </Typography>
              </Box>

              <Tooltip
                title={
                  <>
                    <Typography variant="caption" display="block">
                      Creado: {formatDateTime(patient.createdAt)}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Actualizado: {formatDateTime(patient.updatedAt)}
                    </Typography>
                  </>
                }
              >
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  <TimeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

        <Box display="flex" flexDirection="column" gap={3}>
          {/* Información Personal */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center">
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">
                      Información Personal
                    </Typography>
                  </Box>
                  {!isEditing ? (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      size="small"
                    >
                      Editar
                    </Button>
                  ) : (
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        size="small"
                      >
                        Guardar
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                        size="small"
                      >
                        Cancelar
                      </Button>
                    </Box>
                  )}
                </Box>
                
                <Divider sx={{ mb: 2 }} />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    columnGap: 3,
                    rowGap: 2.5,
                  }}
                >
                  {/* Nombre */}
                  <Box>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Nombre"
                        value={editData.firstName || ''}
                        onChange={(e) => handleEditChange('firstName', e.target.value)}
                        size="small"
                      />
                    ) : (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                          Nombre
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                          {patient.firstName}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Apellido */}
                  <Box>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Apellido"
                        value={editData.lastName || ''}
                        onChange={(e) => handleEditChange('lastName', e.target.value)}
                        size="small"
                      />
                    ) : (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                          Apellido
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                          {patient.lastName}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* RUT */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                      RUT
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                      {patient.identificationNumber}
                    </Typography>
                  </Box>

                  {/* Género */}
                  <Box>
                    {isEditing ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>Género</InputLabel>
                        <Select
                          value={editData.gender || 'M'}
                          onChange={(e) => handleEditChange('gender', e.target.value)}
                          label="Género"
                        >
                          {GENDER_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                          Género
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                          {getGenderLabel(patient.gender)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Fecha de Nacimiento */}
                  <Box>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Fecha de Nacimiento"
                        type="date"
                        value={editData.dateOfBirth || ''}
                        onChange={(e) => handleEditChange('dateOfBirth', e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                          Fecha de Nacimiento
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                          {formatDate(patient.dateOfBirth)} <Typography component="span" variant="caption" color="text.secondary">({calculateAge(patient.dateOfBirth)} años)</Typography>
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Estado */}
                  <Box>
                    {isEditing ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>Estado</InputLabel>
                        <Select
                          value={editData.status || 'activo'}
                          onChange={(e) => handleEditChange('status', e.target.value)}
                          label="Estado"
                        >
                          <MenuItem value="activo">Activo</MenuItem>
                          <MenuItem value="inactivo">Inactivo</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                          Estado
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                          {patient.status === 'activo' ? 'Activo' : 'Inactivo'}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Años de Escolaridad */}
                  <Box>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Años de Escolaridad"
                        type="number"
                        value={editData.educationYears || ''}
                        onChange={(e) => handleEditChange('educationYears', parseInt(e.target.value) || 0)}
                        inputProps={{ min: 0, max: 25 }}
                        size="small"
                      />
                    ) : (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                          Años de Escolaridad
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                          {patient.educationYears ? `${patient.educationYears} años` : 'No especificado'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Información de Contacto */}
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 2 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    Información de Contacto
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" flexDirection="column" gap={2.5}>
                  {/* Email */}
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label="Email"
                      value={editData.email || ''}
                      onChange={(e) => handleEditChange('email', e.target.value)}
                      size="small"
                    />
                  ) : (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                        {patient.email || 'No especificado'}
                      </Typography>
                    </Box>
                  )}

                  {/* Teléfono */}
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label="Teléfono"
                      value={editData.phone || ''}
                      onChange={(e) => handleEditChange('phone', e.target.value)}
                      size="small"
                    />
                  ) : (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                        Teléfono
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                        {patient.phone || 'No especificado'}
                      </Typography>
                    </Box>
                  )}

                  {/* Dirección */}
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label="Dirección"
                      value={editData.address || ''}
                      onChange={(e) => handleEditChange('address', e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                    />
                  ) : (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                        Dirección
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                        {patient.address || 'No especificado'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Sección de Pruebas y Evaluaciones */}
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">
                  Pruebas y Evaluaciones
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddTestModal(true)}
                  size="small"
                >
                  Agregar Prueba
                </Button>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {testsLoading ? (
                <LoadingOrb size={64} minHeight="120px" />
              ) : tests.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Historial de Pruebas ({tests.length})
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {tests.slice(0, 5).map((test) => (
                      <Box 
                        key={test.id} 
                        p={2}
                        sx={{ 
                          border: '1px solid', 
                          borderColor: 'divider', 
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          mb: 1
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {test.testType?.name || getTestTypeCode(test) || 'Prueba'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(test.executedAt).toLocaleDateString('es-CL')} - {test.evaluator?.firstName} {test.evaluator?.lastName}
                            </Typography>
                            {(() => {
                              const testData = getTestData(test);
                              const testTypeCode = getTestTypeCode(test);
                              const isStroop = isScoreBasedTest(testTypeCode);
                              
                              // Mostrar resumen de datos específicos para Stroop
                              if (isStroop) {
                                const scores = [];
                                if (testData.palabraScore !== undefined && testData.palabraScore !== null) {
                                  scores.push(`Palabra: ${testData.palabraScore}`);
                                }
                                if (testData.colorScore !== undefined && testData.colorScore !== null) {
                                  scores.push(`Color: ${testData.colorScore}`);
                                }
                                if (testData.palabraColorScore !== undefined && testData.palabraColorScore !== null) {
                                  scores.push(`Palabra-Color: ${testData.palabraColorScore}`);
                                }
                                if (scores.length > 0) {
                                  return (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      {scores.join(' | ')}
                                    </Typography>
                                  );
                                }
                              } else {
                                // Para TMT, mostrar tiempo si existe
                                if (testData.timeSeconds > 0) {
                                  return (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      Tiempo: {testData.timeSeconds}s
                                      {testData.errors !== null && testData.errors !== undefined && ` | Errores: ${testData.errors}`}
                                    </Typography>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </Box>
                          <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0, flexShrink: 1 }}>
                            {(() => {
                              const testData = getTestData(test);
                              const testTypeCode = getTestTypeCode(test);
                              const isStroop = isScoreBasedTest(testTypeCode);
                              const classifications = getClassificationsFromTest(test);
                              
                              // Colores de clasificación (mismos que la matriz de conversión)
                              const classificationColors: { [key: string]: string } = {
                                'Excepcionalmente Alto': '#dc6eee',
                                'Sobre el Promedio': '#2196F3',
                                'Promedio Alto': '#6dc7f0',
                                'Promedio': '#4CAF50',
                                'Promedio Bajo': '#FFEB3B',
                                'Bajo el Promedio': '#FF9800',
                                'Excepcionalmente Bajo': '#F44336'
                              };

                              // Función para determinar si un color es claro u oscuro (para contrastar texto)
                              const getContrastTextColor = (hexColor: string): string => {
                                // Remover el # si existe
                                const color = hexColor.replace('#', '');
                                
                                // Convertir hex a RGB
                                const r = parseInt(color.substr(0, 2), 16);
                                const g = parseInt(color.substr(2, 2), 16);
                                const b = parseInt(color.substr(4, 2), 16);
                                
                                // Calcular luminancia relativa (fórmula W3C)
                                const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                                
                                // Si la luminancia es mayor a 0.5, el color es claro, usar texto negro
                                // Si es menor a 0.5, el color es oscuro, usar texto blanco
                                return luminance > 0.5 ? '#000000' : '#FFFFFF';
                              };
                              
                              // Para Stroop, mostrar las 4 condiciones de forma compacta
                              // Para TMT, mostrar percentil y clasificación generales
                              if (isStroop) {
                                // Preparar datos de cada condición
                                const conditions = [
                                  {
                                    id: 'palabra',
                                    name: 'Palabra',
                                    shortName: 'P',
                                    score: testData.palabraScore,
                                    percentile: testData.palabraPercentile,
                                    classifications: testData.palabraClassifications
                                  },
                                  {
                                    id: 'color',
                                    name: 'Color',
                                    shortName: 'C',
                                    score: testData.colorScore,
                                    percentile: testData.colorPercentile,
                                    classifications: testData.colorClassifications
                                  },
                                  {
                                    id: 'palabraColor',
                                    name: 'Palabra-Color',
                                    shortName: 'PC',
                                    score: testData.palabraColorScore,
                                    percentile: testData.palabraColorPercentile,
                                    classifications: testData.palabraColorClassifications
                                  },
                                  {
                                    id: 'interferencia',
                                    name: 'Interferencia',
                                    shortName: 'I',
                                    score: testData.interferenciaScore,
                                    percentile: testData.interferenciaPercentile,
                                    classifications: testData.interferenciaClassifications
                                  }
                                ].filter(c => c.score !== undefined && c.score !== null);

                                return (
                                  <>
                                    <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" sx={{ maxWidth: { xs: '100%', sm: '400px' } }}>
                                      {conditions.map((condition) => {
                                        const hasMultipleClassifications = condition.classifications && condition.classifications.length > 1;
                                        const mainClassification = condition.classifications && condition.classifications.length > 0 
                                          ? condition.classifications[0] 
                                          : null;
                                        const classificationColor = mainClassification 
                                          ? classificationColors[mainClassification] || '#757575'
                                          : '#757575';
                                        
                                        // Determinar color de texto según luminancia del fondo
                                        const textColor = getContrastTextColor(classificationColor);
                                        
                                        // Para gradientes con múltiples clasificaciones, usar el color más claro como referencia
                                        let finalTextColor = textColor;
                                        if (hasMultipleClassifications && condition.classifications && condition.classifications.length > 1) {
                                          const secondColor = classificationColors[condition.classifications[1]] || classificationColor;
                                          const secondTextColor = getContrastTextColor(secondColor);
                                          // Si ambos requieren texto negro, usar negro; si no, usar blanco
                                          finalTextColor = (textColor === '#000000' && secondTextColor === '#000000') ? '#000000' : '#FFFFFF';
                                        }
                                        
                                        // Construir label compacto: "PC P75" o "P P50"
                                        const chipLabel = condition.percentile !== null && condition.percentile !== undefined
                                          ? `${condition.shortName} P${condition.percentile}`
                                          : condition.shortName;
                                        
                                        // Tooltip con información completa
                                        const tooltipTitle = (
                                          <Box>
                                            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                                              {condition.name}
                                            </Typography>
                                            {condition.score !== undefined && condition.score !== null && (
                                              <Typography variant="caption" display="block">
                                                Puntuación: {condition.score}
                                              </Typography>
                                            )}
                                            {condition.percentile !== null && condition.percentile !== undefined && (
                                              <Typography variant="caption" display="block">
                                                Percentil: P{condition.percentile}
                                              </Typography>
                                            )}
                                            {condition.classifications && condition.classifications.length > 0 && (
                                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                Clasificación: {condition.classifications.join(' / ')}
                                              </Typography>
                                            )}
                                          </Box>
                                        );

                                        return (
                                          <Tooltip key={condition.id} title={tooltipTitle} arrow placement="top">
                                            <Chip
                                              label={chipLabel}
                                              size="small"
                                              variant="filled"
                                              sx={{
                                                height: 24,
                                                fontSize: '0.7rem',
                                                fontWeight: 'bold',
                                                bgcolor: classificationColor,
                                                color: finalTextColor,
                                                '& .MuiChip-label': {
                                                  px: 0.75,
                                                  py: 0,
                                                  fontSize: '0.7rem'
                                                },
                                                ...(hasMultipleClassifications && {
                                                  background: `linear-gradient(90deg, ${classificationColor} 0%, ${classificationColors[condition.classifications![1]] || classificationColor} 100%)`
                                                })
                                              }}
                                            />
                                          </Tooltip>
                                        );
                                      })}
                                    </Box>
                                    <Button
                                      size="small"
                                      startIcon={<VisibilityIcon />}
                                      onClick={() => handleShowObservations(test)}
                                      sx={{ minWidth: 'auto', px: 1, ml: 0.5, flexShrink: 0 }}
                                    >
                                      Ver
                                    </Button>
                                  </>
                                );
                              } else {
                                // Para TMT, mostrar como antes con contraste de texto adecuado
                                return (
                                  <>
                                    {classifications.length > 0 && (
                                      classifications.length > 1 ? (
                                        (() => {
                                          const color1 = classificationColors[classifications[0]] || '#757575';
                                          const color2 = classificationColors[classifications[1]] || '#757575';
                                          const textColor1 = getContrastTextColor(color1);
                                          const textColor2 = getContrastTextColor(color2);
                                          const finalTextColor = (textColor1 === '#000000' && textColor2 === '#000000') ? '#000000' : '#FFFFFF';
                                          
                                          return (
                                            <Chip 
                                              label={classifications.join(' / ')}
                                              size="small"
                                              variant="filled"
                                              sx={{
                                                background: `linear-gradient(90deg, ${color1} 0%, ${color2} 100%)`,
                                                color: finalTextColor,
                                                fontWeight: 'bold'
                                              }}
                                            />
                                          );
                                        })()
                                      ) : (
                                        (() => {
                                          const bgColor = classificationColors[classifications[0]] || '#757575';
                                          const textColor = getContrastTextColor(bgColor);
                                          
                                          return (
                                            <Chip 
                                              label={classifications[0]}
                                              size="small"
                                              variant="filled"
                                              sx={{
                                                bgcolor: bgColor,
                                                color: textColor,
                                                fontWeight: 'bold'
                                              }}
                                            />
                                          );
                                        })()
                                      )
                                    )}
                                    {testData.percentile && (
                                      <Chip 
                                        label={`P${testData.percentile}`}
                                        size="small"
                                        color="info"
                                        variant="filled"
                                        sx={{
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    )}
                                    <Button
                                      size="small"
                                      startIcon={<VisibilityIcon />}
                                      onClick={() => handleShowObservations(test)}
                                      sx={{ minWidth: 'auto', px: 1 }}
                                    >
                                      Ver
                                    </Button>
                                  </>
                                );
                              }
                            })()}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  {tests.length > 5 && (
                    <Button
                      variant="outlined"
                      component={RouterLink}
                      to={`/patients/${patient.id}/evaluations`}
                      sx={{ mt: 2 }}
                      size="small"
                    >
                      Ver Todas las Pruebas ({tests.length})
                    </Button>
                  )}
                </Box>
              ) : (
                <Box textAlign="center" py={3}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No hay pruebas registradas para este paciente
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Agrega la primera prueba usando el botón "Agregar Prueba"
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Modal para agregar prueba */}
      {patient && (
        <AddTestModal
          open={showAddTestModal}
          onClose={() => setShowAddTestModal(false)}
          patientId={patient.id}
          patientName={`${patient.firstName} ${patient.lastName}`}
          patientEducationYears={patient.educationYears}
          patientDateOfBirth={patient.dateOfBirth}
          onTestAdded={handleTestAdded}
        />
      )}

      {/* Modal para mostrar detalle de prueba */}
      {selectedTest && (
        <ObservationsModal
          open={showObservationsModal}
          onClose={() => setShowObservationsModal(false)}
          test={selectedTest}
          patientName={`${patient?.firstName} ${patient?.lastName}`}
          onTestUpdated={handleTestUpdated}
        />
      )}
    </Container>
  );
};

export default PatientDetailPage; 